// File: client/src/components/rules/SmartRulesView.js
import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, Typography, message, Modal, Input, Select, Tabs, Spin, Card, Tooltip } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useSharedData } from '../contexts/SharedDataContext';
import {
    PlusOutlined,
    LoadingOutlined,
    PlayCircleOutlined,
    CodeOutlined,
    SaveOutlined,
    DeleteOutlined,
    EditOutlined,
    SettingOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';

import WorkflowCanvas from './WorkflowCanvas';
import WorkflowConfigurationManager from './WorkflowConfigurationManager';
import NodeConfigModal from './NodeConfigModal';
import { defaultWorkflowConfig } from './workflowDefaults';
import TestWorkflowModal from './TestWorkflowModal';
import MonitoringView from './MonitoringView';
import WorkflowEditorWrapper from './WorkflowEditorWrapper';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

// Custom spinner icon
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const SmartRulesView = ({ selectedCompanyNames }) => {
    // State
    const [activeTab, setActiveTab] = useState('canvas');
    const [loading, setLoading] = useState(false);
    const [workflows, setWorkflows] = useState([]);
    const [currentWorkflow, setCurrentWorkflow] = useState(null);
    const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [isTestModalVisible, setIsTestModalVisible] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Auth and global data
    const { getAccessTokenSilently } = useAuth0();
    const { selectedTenant } = useSharedData();

    // Refs
    const workflowRef = useRef(null);

    // Fetch workflows on component mount or tenant change
    useEffect(() => {
        fetchWorkflows();
    }, [selectedTenant]);

    // Fetch workflows
    const fetchWorkflows = async () => {
        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.get('/api/workflows', {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-tenant': selectedTenant
                }
            });

            setWorkflows(response.data);

            // Select first workflow or create default
            if (response.data.length > 0) {
                setCurrentWorkflow(response.data[0]);
            } else {
                createNewWorkflow();
            }
        } catch (error) {
            console.error('Error fetching workflows:', error);
            message.error('Failed to load workflows');
        } finally {
            setLoading(false);
        }
    };

    // Create a new workflow
    const createNewWorkflow = () => {
        const newWorkflow = {
            ...defaultWorkflowConfig,
            name: `New Workflow ${workflows.length + 1}`,
            isNew: true
        };

        setCurrentWorkflow(newWorkflow);
        setHasUnsavedChanges(true);
    };

    // Save current workflow
    const saveWorkflow = async () => {
        if (!currentWorkflow) return;

        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            let response;

            // Prepare workflow data
            const workflowData = {
                ...currentWorkflow,
                tenant: selectedTenant,
                updatedAt: new Date().toISOString()
            };

            if (currentWorkflow.isNew || !currentWorkflow.id) {
                // Create new workflow
                response = await axios.post('/api/workflows', workflowData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': selectedTenant
                    }
                });

                // Update workflows list
                setWorkflows([...workflows, response.data]);
            } else {
                // Update existing workflow
                response = await axios.put(`/api/workflows/${currentWorkflow.id}`, workflowData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': selectedTenant
                    }
                });

                // Update workflows list
                setWorkflows(workflows.map(w => w.id === response.data.id ? response.data : w));
            }

            // Update current workflow
            setCurrentWorkflow(response.data);
            setHasUnsavedChanges(false);
            message.success('Workflow saved successfully');
        } catch (error) {
            console.error('Error saving workflow:', error);
            message.error('Failed to save workflow');
        } finally {
            setLoading(false);
        }
    };

    // Delete current workflow
    const deleteWorkflow = async () => {
        if (!currentWorkflow || !currentWorkflow.id) return;

        Modal.confirm({
            title: 'Delete Workflow',
            content: `Are you sure you want to delete "${currentWorkflow.name}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                setLoading(true);
                try {
                    const token = await getAccessTokenSilently();
                    await axios.delete(`/api/workflows/${currentWorkflow.id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'x-tenant': selectedTenant
                        }
                    });

                    // Update workflows list
                    const updatedWorkflows = workflows.filter(w => w.id !== currentWorkflow.id);
                    setWorkflows(updatedWorkflows);

                    // Select another workflow or create default
                    if (updatedWorkflows.length > 0) {
                        setCurrentWorkflow(updatedWorkflows[0]);
                    } else {
                        createNewWorkflow();
                    }

                    message.success('Workflow deleted successfully');
                } catch (error) {
                    console.error('Error deleting workflow:', error);
                    message.error('Failed to delete workflow');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // Change current workflow
    const handleWorkflowChange = (workflowId) => {
        if (hasUnsavedChanges) {
            Modal.confirm({
                title: 'Unsaved Changes',
                content: 'You have unsaved changes. Do you want to discard them?',
                okText: 'Discard',
                cancelText: 'Stay on this workflow',
                onOk: () => {
                    const selected = workflows.find(w => w.id === workflowId);
                    if (selected) {
                        setCurrentWorkflow(selected);
                        setHasUnsavedChanges(false);
                    }
                }
            });
        } else {
            const selected = workflows.find(w => w.id === workflowId);
            if (selected) {
                setCurrentWorkflow(selected);
            }
        }
    };

    // Handle workflow updates
    const handleWorkflowUpdate = (updatedWorkflow) => {
        setCurrentWorkflow(updatedWorkflow);
        setHasUnsavedChanges(true);
    };

    // Open node configuration modal
    const handleNodeConfig = (node) => {
        setSelectedNode(node);
        setIsConfigModalVisible(true);
    };

    // Update node configuration
    const handleUpdateNodeConfig = (updatedNode) => {
        if (!currentWorkflow || !workflowRef.current) return;

        // Update node in the workflow
        workflowRef.current.updateNode(updatedNode);

        // Close modal
        setIsConfigModalVisible(false);
        setSelectedNode(null);

        // Mark as unsaved
        setHasUnsavedChanges(true);
    };

    // Render the main view when loading is complete
    const renderContent = () => {
        if (loading && workflows.length === 0) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                    <Spin indicator={antIcon} />
                    <Text style={{ marginLeft: 12 }}>Loading workflows...</Text>
                </div>
            );
        }

        return (
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                tabBarExtraContent={
                    <Space>
                        {activeTab === 'canvas' && (
                            <>
                                <Button
                                    icon={<PlayCircleOutlined />}
                                    onClick={() => setIsTestModalVisible(true)}
                                    disabled={!currentWorkflow || hasUnsavedChanges}
                                >
                                    Test Workflow
                                </Button>
                                <Button
                                    type={hasUnsavedChanges ? "primary" : "default"}
                                    icon={<SaveOutlined />}
                                    onClick={saveWorkflow}
                                    disabled={!currentWorkflow || !hasUnsavedChanges}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </Space>
                }
            >
                <TabPane tab="Workflow Canvas" key="canvas">
                    <div style={{ marginBottom: 16 }}>
                        <WorkflowCanvas
                            ref={workflowRef}
                            workflow={currentWorkflow}
                            onWorkflowChange={handleWorkflowUpdate}
                            onNodeConfig={handleNodeConfig}
                        />
                    </div>
                </TabPane>

                <TabPane tab="Workflow Monitoring" key="monitoring">
                    <MonitoringView
                        workflows={workflows}
                        currentWorkflowId={currentWorkflow?.id}
                        onSelectWorkflow={handleWorkflowChange}
                    />
                </TabPane>
            </Tabs>
        );
    };

    return (
        <WorkflowEditorWrapper>
        <div style={{ padding: '24px', overflowY: 'scroll' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                {/* Header with workflow selector */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <WorkflowConfigurationManager
                        workflows={workflows}
                        currentWorkflow={currentWorkflow}
                        onWorkflowChange={handleWorkflowChange}
                        onCreateWorkflow={createNewWorkflow}
                        onDeleteWorkflow={deleteWorkflow}
                        hasUnsavedChanges={hasUnsavedChanges}
                        onSaveWorkflow={saveWorkflow}
                    />
                </div>

                {/* Main content */}
                <Card
                    style={{
                        background: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, .1)',
                        border: 'none',
                    }}
                    bodyStyle={{ padding: '24px' }}
                >
                    {renderContent()}
                </Card>
            </Space>

            {/* Node configuration modal */}
            <NodeConfigModal
                visible={isConfigModalVisible}
                node={selectedNode}
                onClose={() => setIsConfigModalVisible(false)}
                onUpdate={handleUpdateNodeConfig}
            />

            {/* Test workflow modal */}
            <TestWorkflowModal
                visible={isTestModalVisible}
                workflow={currentWorkflow}
                onClose={() => setIsTestModalVisible(false)}
            />
        </div>
        </WorkflowEditorWrapper>
    );
};

export default SmartRulesView;
